import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '..';
import { ActiveTimeEntryData } from '@/modules/TimeEntry/components/ActiveTimeEntry';

interface TimerState {
  isActive: boolean;
  startTime: number | null;
  activeEntry: ActiveTimeEntryData | null;
  elapsedTime: number;
  lastUpdated: number;
}

const initialState: TimerState = {
  isActive: false,
  startTime: null,
  activeEntry: null,
  elapsedTime: 0,
  lastUpdated: 0
};

export const timerSlice = createSlice({
  name: 'timer',
  initialState,
  reducers: {
    startTimer: (state, action: PayloadAction<ActiveTimeEntryData>) => {
      state.isActive = true;
      state.startTime = Date.now();
      state.lastUpdated = Date.now();
      state.activeEntry = action.payload;
      state.elapsedTime = 0;
    },
    stopTimer: (state) => {
      state.isActive = false;
      state.startTime = null;
      state.activeEntry = null;
      state.elapsedTime = 0;
      state.lastUpdated = 0;
    },
    updateElapsedTime: (state) => {
      if (state.isActive && state.startTime) {
        const now = Date.now();
        // Calculate elapsed time since last update
        const delta = now - state.lastUpdated;
        // Add to total elapsed time
        state.elapsedTime += delta;
        // Update last updated timestamp
        state.lastUpdated = now;
      }
    },
    updateActiveEntry: (state, action: PayloadAction<Partial<ActiveTimeEntryData>>) => {
      if (state.activeEntry) {
        state.activeEntry = {
          ...state.activeEntry,
          ...action.payload
        };
      }
    },
    updateStartTime: (state, action: PayloadAction<number>) => {
      if (state.isActive) {
        state.startTime = action.payload;
        state.lastUpdated = Date.now();
        state.elapsedTime = Date.now() - action.payload;
      }
    }
  }
});

export const { startTimer, stopTimer, updateElapsedTime, updateActiveEntry, updateStartTime } = timerSlice.actions;

export const selectTimerState = (state: RootState) => state.timer;
export const selectIsTimerActive = (state: RootState) => state.timer.isActive;
export const selectActiveEntry = (state: RootState) => state.timer.activeEntry;
export const selectElapsedTime = (state: RootState) => state.timer.elapsedTime;

export default timerSlice.reducer;
